import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import media from 'styled-media-query';
import { themeGet, breakpointsObject as bp } from '@firsttable/web-theme';
import { Col, Row } from '@firsttable/web-components/atoms';
import RowHead from '@firsttable/web-components/molecules/RowHead';
import ContainerContent from '@firsttable/web-components/molecules/ContainerContent';
import { FAQ } from '@firsttable/web-components/molecules';
import ContactCTA from '@firsttable/web-components/molecules/ContactCTA';
import { Page } from '@firsttable/types';
import parseHtml from '../helpers/parseHtml';
import NavButton from '../components/atoms/NavButton';
import GenericLayout from './GenericLayout';
import SEO from '../components/seo';

interface QuestionAnswer {
  question: string;
  answer: string;
}
const DesktopQuestions = styled(Row)`
  ${media.lessThan(bp.m)`
    display: none;
  `};
`;

const MobileQuestions = styled(Row)`
  display: none;
  ${media.lessThan(bp.m)`
    display: block;
  `};
`;

const FAQLayout = ({
  pageData,
  faqs,
}: {
  pageData: Page;
  faqs: QuestionAnswer[];
}) => {
  const borderLine = `1px solid ${themeGet('colors.grayscale.300')}`;

  return (
    <GenericLayout>
      <SEO
        id={pageData.foreignId}
        title={pageData.metaTitleFormatted}
        description={pageData.metaDescription}
        ogImage={pageData.ogImage}
        metaTags={pageData.metaTags}
        slug={pageData.slug}
      />
      <ContainerContent>
        <RowHead title="Frequently asked questions" />
        <DesktopQuestions gap={0}>
          <Col
            width={[1, 1 / 2, 1 / 3]}
            borderLeft={[null, borderLine]}
            borderBottom={[null, borderLine]}
          >
            {faqs
              // @ts-ignore
              .filter((value: any, index: number) => index % 3 === 0)
              .map(
                ({ question: q1, answer: a1 }: QuestionAnswer, i: number) => (
                  <FAQ key={i} body={parseHtml(a1)} title={q1} />
                ),
              )}
          </Col>
          <Col
            width={[1, 1 / 2, 1 / 3]}
            borderLeft={[null, borderLine]}
            borderBottom={[null, borderLine]}
            borderRight={[null, borderLine, 'none']}
          >
            {faqs
              .slice(1, faqs.length - 1)
              // @ts-ignore
              .filter((value: any, index: number) => index % 3 === 0)
              .map(
                ({ question: q2, answer: a2 }: QuestionAnswer, i: number) => (
                  <FAQ key={i} body={parseHtml(a2)} title={q2} />
                ),
              )}
            <ContactCTA
              display={['none', 'none', 'block']}
              NavButtonComponent={NavButton}
            />
          </Col>
          <Col
            mt={[-1, null, 0]}
            width={[1, 1, 1 / 3]}
            borderLeft={[null, borderLine]}
            borderBottom={[null, borderLine]}
            borderRight={[null, borderLine]}
          >
            {faqs
              .slice(2, faqs.length)
              // @ts-ignore
              .filter((value: any, index: number) => index % 3 === 0)
              .map(
                ({ question: q3, answer: a3 }: QuestionAnswer, i: number) => (
                  <FAQ key={i} body={parseHtml(a3)} title={q3} />
                ),
              )}
            <ContactCTA
              display={['block', 'block', 'none']}
              mt="l"
              NavButtonComponent={NavButton}
            />
          </Col>
        </DesktopQuestions>
        <MobileQuestions gap={0}>
          <Col
            width={1}
            borderLeft={[null, borderLine]}
            borderRight={[null, borderLine]}
          >
            {faqs.map(
              ({ question: q4, answer: a4 }: QuestionAnswer, i: number) => (
                <FAQ key={i} body={parseHtml(a4)} title={q4} />
              ),
            )}
            <ContactCTA
              display={['block', 'block', 'none']}
              NavButtonComponent={NavButton}
              mt="l"
            />
          </Col>
        </MobileQuestions>
      </ContainerContent>
    </GenericLayout>
  );
};

FAQLayout.propTypes = {
  pageData: PropTypes.object,
  faqs: PropTypes.array,
};
FAQLayout.defaultProps = {};

export default FAQLayout;
